/**
 * MedicielRealtimeTab — Admin tab to manage the Mediciel real-time API
 * integration: trigger manual syncs, see the last sync log, search the
 * locally-cached product catalog and run quick connectivity tests.
 */
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  RefreshCw,
  CheckCircle2,
  AlertCircle,
  Loader2,
  Search,
  Package,
  Clock,
  TrendingUp,
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const fcfa = (n) => new Intl.NumberFormat('fr-FR').format(n || 0) + ' FCFA';

const formatDateTime = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString('fr-FR', {
      day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
    });
  } catch { return ''; }
};

/**
 * Transforme un message d'erreur Mediciel brut (cURL / HTTP / réseau)
 * en message clair en français, adapté à l'utilisateur final.
 * Retourne un objet { title, description } ou null pour fallback générique.
 */
const formatMedicielError = (err, lastSyncIso) => {
  const detail = err?.response?.data?.detail
    || err?.response?.data?.error
    || err?.message
    || '';
  const lower = String(detail).toLowerCase();
  const lastSync = lastSyncIso ? formatDateTime(lastSyncIso) : null;
  const fallbackHint = lastSync
    ? `Vos produits du dernier sync (${lastSync}) restent disponibles — vous pouvez continuer à travailler.`
    : '';

  // Erreurs de connexion réseau (cURL 7 / 28 / timeout / refused)
  if (
    lower.includes('curl error 7')
    || lower.includes('failed to connect')
    || lower.includes('could not connect')
    || lower.includes('connection refused')
    || lower.includes('connection reset')
    || lower.includes('connection timed out')
    || lower.includes('econnrefused')
  ) {
    return {
      title: 'Mediciel injoignable',
      description: `Le serveur Mediciel ne répond pas. Vérifiez que la machine Mediciel à la pharmacie est allumée et connectée à Internet (port forwarding actif). ${fallbackHint}`.trim(),
    };
  }

  // Hôte introuvable / DNS
  if (lower.includes('could not resolve host') || lower.includes('curl error 6')) {
    return {
      title: 'Adresse Mediciel introuvable',
      description: `L'adresse du serveur Mediciel est invalide ou son IP a changé. Vérifiez la configuration. ${fallbackHint}`.trim(),
    };
  }

  // Timeout
  if (lower.includes('timeout') || lower.includes('curl error 28')) {
    return {
      title: 'Mediciel ne répond pas à temps',
      description: `Le serveur Mediciel met trop de temps à répondre. Internet pharmacie lent ou serveur Mediciel surchargé. ${fallbackHint}`.trim(),
    };
  }

  // Authentification
  const status = err?.response?.status;
  if (status === 401 || lower.includes('unauthorized') || lower.includes('token')) {
    return {
      title: 'Token Mediciel invalide',
      description: 'Le jeton d\'authentification Mediciel est expiré ou incorrect. Mettez-le à jour dans la configuration.',
    };
  }
  if (status === 403) {
    return {
      title: 'Accès Mediciel refusé',
      description: 'Le compte Mediciel utilisé n\'a pas les droits API. Contactez votre intégrateur Mediciel.',
    };
  }

  return null;
};

const MedicielRealtimeTab = () => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [testing, setTesting] = useState(false);

  const [search, setSearch] = useState('');
  const [products, setProducts] = useState([]);
  const [searching, setSearching] = useState(false);

  const authHeaders = () => {
    const token = localStorage.getItem('dermascan_token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const fetchStatus = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/mediciel-realtime/status`, { headers: authHeaders() });
      setStatus(res.data);
    } catch (e) {
      toast.error("Erreur lors du chargement du statut Mediciel");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  const runSync = async () => {
    setSyncing(true);
    try {
      const res = await axios.post(`${API}/mediciel-realtime/sync`, {}, { headers: authHeaders(), timeout: 180000 });
      toast.success(`Sync OK · ${res.data.products_count} produits en ${res.data.duration_seconds.toFixed(1)} s`);
      await fetchStatus();
    } catch (e) {
      const friendly = formatMedicielError(e, status?.last_sync_at);
      if (friendly) {
        toast.error(friendly.title, { description: friendly.description, duration: 8000 });
      } else {
        toast.error(e.response?.data?.detail || 'Échec de la synchronisation');
      }
    } finally {
      setSyncing(false);
    }
  };

  const runTest = async () => {
    setTesting(true);
    try {
      const res = await axios.post(`${API}/mediciel-realtime/test`, {}, { headers: authHeaders() });
      toast.success(`Connexion OK · ${res.data.raw_count} produits trouvés pour "DOLIPRANE"`);
    } catch (e) {
      const friendly = formatMedicielError(e, status?.last_sync_at);
      if (friendly) {
        toast.error(friendly.title, { description: friendly.description, duration: 8000 });
      } else {
        toast.error(e.response?.data?.detail || 'Connexion échouée');
      }
    } finally {
      setTesting(false);
    }
  };

  const runSearch = async (e) => {
    e?.preventDefault?.();
    if (!search.trim()) {
      setProducts([]);
      return;
    }
    setSearching(true);
    try {
      const res = await axios.get(`${API}/mediciel-realtime/products`, {
        headers: authHeaders(),
        params: { search: search.trim(), limit: 50 },
      });
      setProducts(res.data.products || []);
    } catch (e) {
      toast.error("Erreur de recherche");
    } finally {
      setSearching(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-[#1B4D3E]" />
      </div>
    );
  }

  const cfg = status?.config || {};
  const allConfigured = cfg.url_configured && cfg.token_configured && cfg.id_user_configured && cfg.idmediciel_client_configured;
  const last = status?.last_sync;

  return (
    <div className="space-y-5" data-testid="mediciel-realtime-tab">
      <div>
        <h2 className="text-xl font-serif font-semibold text-zinc-900">Stock Mediciel · Temps réel</h2>
        <p className="text-sm text-zinc-500">
          Synchronisation directe avec votre serveur Mediciel via API REST. Met à jour le catalogue complet (stock, prix, péremption, emplacement…)
        </p>
      </div>

      {/* Configuration */}
      <div className={`rounded-lg border p-4 ${allConfigured ? 'bg-emerald-50 border-emerald-200' : 'bg-orange-50 border-orange-200'}`}>
        <div className="flex items-start gap-3">
          {allConfigured ? (
            <CheckCircle2 className="w-5 h-5 text-emerald-600 mt-0.5" />
          ) : (
            <AlertCircle className="w-5 h-5 text-[#C45C26] mt-0.5" />
          )}
          <div className="flex-1">
            <p className={`text-sm font-semibold ${allConfigured ? 'text-emerald-900' : 'text-[#C45C26]'}`}>
              {allConfigured ? 'Configuration complète' : 'Configuration incomplète'}
            </p>
            <ul className="text-xs text-zinc-600 mt-1 space-y-0.5">
              <li>URL endpoint : {cfg.url_configured ? '✅' : '❌ manquante'}</li>
              <li>Token Bearer : {cfg.token_configured ? '✅' : '❌ manquant'}</li>
              <li>ID utilisateur : {cfg.id_user_configured ? '✅' : '❌ manquant'}</li>
              <li>ID client Mediciel : {cfg.idmediciel_client_configured ? '✅' : '❌ manquant'}</li>
            </ul>
            <p className="text-xs text-zinc-500 mt-2">Variables stockées en sécurité dans le fichier .env du serveur — jamais exposées au navigateur.</p>
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="flex flex-wrap gap-2">
        <Button
          onClick={runSync}
          disabled={syncing || !allConfigured}
          className="bg-[#1B4D3E] hover:bg-[#1B4D3E]/90 text-white"
          data-testid="mediciel-realtime-sync-btn"
        >
          {syncing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
          {syncing ? 'Synchronisation…' : 'Synchroniser le stock maintenant'}
        </Button>
        <Button
          variant="outline"
          onClick={runTest}
          disabled={testing || !allConfigured}
          data-testid="mediciel-realtime-test-btn"
        >
          {testing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
          Tester la connexion
        </Button>
      </div>

      {/* KPIs last sync */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div className="rounded-lg border border-zinc-200 bg-white p-4">
          <p className="text-xs uppercase tracking-wide text-zinc-500 font-semibold">Produits en cache</p>
          <p className="text-2xl font-mono font-bold text-zinc-900 mt-1 tabular-nums">
            {(status?.cached_products || 0).toLocaleString('fr-FR')}
          </p>
        </div>
        <div className="rounded-lg border border-zinc-200 bg-white p-4">
          <p className="text-xs uppercase tracking-wide text-zinc-500 font-semibold">Dernière sync</p>
          <p className="text-sm text-zinc-900 mt-1">
            {last?.finished_at
              ? new Date(last.finished_at).toLocaleString('fr-FR', { dateStyle: 'medium', timeStyle: 'short' })
              : 'Jamais'}
          </p>
          {last?.status === 'success' && (
            <Badge className="mt-1 bg-emerald-100 text-emerald-700 border-emerald-200">
              <CheckCircle2 className="w-3 h-3 mr-1" /> Succès
            </Badge>
          )}
          {last?.status === 'error' && (
            <Badge className="mt-1 bg-red-100 text-red-700 border-red-200">
              <AlertCircle className="w-3 h-3 mr-1" /> Erreur
            </Badge>
          )}
        </div>
        <div className="rounded-lg border border-zinc-200 bg-white p-4">
          <p className="text-xs uppercase tracking-wide text-zinc-500 font-semibold">Durée dernière sync</p>
          <p className="text-2xl font-mono font-bold text-zinc-900 mt-1 tabular-nums">
            {last?.duration_seconds ? `${last.duration_seconds.toFixed(1)} s` : '—'}
          </p>
        </div>
      </div>

      {last?.status === 'error' && last?.error && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800">
          <p className="font-semibold">Détail de l'erreur</p>
          <p className="font-mono text-xs mt-1 break-all">{last.error}</p>
        </div>
      )}

      {/* Search */}
      <div className="rounded-lg border border-zinc-200 bg-white p-4">
        <h3 className="text-sm font-semibold text-zinc-900 mb-3 flex items-center gap-2">
          <Search className="w-4 h-4" /> Rechercher un produit dans le cache
        </h3>
        <form onSubmit={runSearch} className="flex gap-2">
          <Input
            placeholder="Nom ou code-barres (ex : DOLIPRANE, 3582910...)"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="mediciel-realtime-search-input"
          />
          <Button type="submit" disabled={searching} className="bg-[#1B4D3E] hover:bg-[#1B4D3E]/90 text-white">
            {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Rechercher'}
          </Button>
        </form>

        {products.length > 0 && (
          <div className="mt-4 overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-zinc-50 border-b border-zinc-200">
                <tr className="text-left text-xs uppercase tracking-wide text-zinc-500">
                  <th className="px-3 py-2 font-medium">Nom</th>
                  <th className="px-3 py-2 font-medium">EAN</th>
                  <th className="px-3 py-2 font-medium text-right">Stock total</th>
                  <th className="px-3 py-2 font-medium text-right">PV TTC</th>
                  <th className="px-3 py-2 font-medium">Rotation</th>
                  <th className="px-3 py-2 font-medium">Péremption</th>
                </tr>
              </thead>
              <tbody>
                {products.map((p) => (
                  <tr key={p.mediciel_id} className="border-b border-zinc-100 hover:bg-zinc-50">
                    <td className="px-3 py-2 text-zinc-900">{p.name}</td>
                    <td className="px-3 py-2 font-mono text-xs text-zinc-500">{p.code_ean}</td>
                    <td className={`px-3 py-2 text-right font-mono tabular-nums ${p.stock_total <= 0 ? 'text-red-700 font-semibold' : p.stock_total <= p.reorder_min ? 'text-[#C45C26] font-semibold' : 'text-zinc-700'}`}>
                      {p.stock_total}
                    </td>
                    <td className="px-3 py-2 text-right font-mono tabular-nums text-xs text-zinc-700">{fcfa(p.selling_price_ttc)}</td>
                    <td className="px-3 py-2">
                      {p.rotation_category && (
                        <Badge className={`${
                          p.rotation_category === 'A' ? 'bg-emerald-100 text-emerald-700 border-emerald-200'
                          : p.rotation_category === 'B' ? 'bg-blue-100 text-blue-700 border-blue-200'
                          : p.rotation_category === 'C' ? 'bg-orange-100 text-[#C45C26] border-orange-200'
                          : 'bg-zinc-100 text-zinc-700 border-zinc-200'
                        }`}>
                          {p.rotation_category}
                        </Badge>
                      )}
                    </td>
                    <td className="px-3 py-2 font-mono text-xs text-zinc-600">{p.expiration_date || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {products.length === 0 && search.trim() && !searching && (
          <p className="text-center text-sm text-zinc-500 mt-4">Aucun produit trouvé</p>
        )}
      </div>
    </div>
  );
};

export default MedicielRealtimeTab;
